import { action } from 'satcheljs';
import Permission from 'owa-service/lib/contract/Permission';
import { SharedFolderActionSource } from '../util/folderConstants';
import BaseFolderType from 'owa-service/lib/contract/BaseFolderType';
import FolderStore from '../store/schema/FolderStore';

/**
 * Action to get archive folders.
 */
export const getArchiveFolders = action('GET_ARCHIVE_FOLDERS', (source: string) => ({ source }));

/**
 * Action to get shared folders for a particular user.
 * @param principalSMTPAddress The email id to identify for which root we are getting shared folders.
 * @param actionSource Source from where this action is called.
 */
export const getSharedFolders = action(
    'GET_SHARED_FOLDERS',
    (principalSMTPAddress: string, actionSource: SharedFolderActionSource) => ({
        principalSMTPAddress,
        actionSource,
    })
);

/**
 * Action to get primary mail folders.
 */
export const getPrimaryMailFolders = action('GET_PRIMARY_MAIL_FOLDERS');

/**
 * Action to get all shared folders users email and root folder display name.
 */
export const initializeSharedFolderRoots = action('INITIALIZE_SHARED_FOLDER_ROOTS');

/**
 * Action to add a new shared folder user.
 */
export const addSharedFolderUser = action(
    'ADD_SHARED_FOLDER_USER',
    (displayName: string, principalSMTPAddress: string, skipServerValidation?: boolean) => ({
        displayName,
        principalSMTPAddress,
        skipServerValidation,
    })
);

/**
 * Action to remove a shared folder user.
 */
export const removeSharedFolderUser = action(
    'REMOVE_SHARED_FOLDER_USER',
    (principalSMTPAddress: string) => ({ principalSMTPAddress })
);

/**
 * Action to initialize archive root folder from session data.
 */
export const initializeArchiveMailboxFromSessionData = action(
    'INITIALIZE_ARCHIVE_MAILBOX_FROM_SESSIONDATA'
);

/**
 * Action to show error dialog and toggle the folder expansion state
 * @param principalSMTPAddress The email id to identify for which root we are getting shared folders.
 * @param actionSource Source from where this action is called.
 */
export const onSharedFolderFetchFailed = action(
    'ON_SHARED_FOLDER_FETCH_FAILED',
    (principalSMTPAddress: string, actionSource: SharedFolderActionSource) => ({
        principalSMTPAddress,
        actionSource,
    })
);

/**
 * Action to fetch folder permissions from server.
 * @param folderId Id of the folder
 */
export const getFolderPermissions = action('GET_FOLDER_PERMISSIONS', (folderId: string) => ({
    folderId,
}));

/**
 * Action to dispatch success status for fetch folder permissions request.
 */
export const getFolderPermissionsSuccess = action('GET_FOLDER_PERMISSIONS_SUCCESS');

/**
 * Action to dispatch failure status for fetch folder permissions request.
 */
export const getFolderPermissionsFailed = action('GET_FOLDER_PERMISSIONS_FAILED');

/**
 * Action to update folder permissions on server.
 * @param folderId Id of the folder
 * @param permissions Permissions defined on the folder
 */
export const updateFolderPermissions = action(
    'UPDATE_FOLDER_PERMISSIONS',
    (folderId: string, permissions: Permission[]) => ({ folderId, permissions })
);

/**
 * Action to update folder permissions in store.
 * @param folderId Id of the folder
 * @param permissions Permissions defined on the folder
 */
export const updateFolderPermissionsInStore = action(
    'UPDATE_FOLDER_PERMISSIONS_IN_STORE',
    (folderId: string, permissions: Permission[]) => ({ folderId, permissions })
);

/**
 * Action to dispatch success status for update folder permissions request.
 */
export const updateFolderPermissionsSuccess = action('UPDATE_FOLDER_PERMISSIONS_SUCCESS');

/**
 * Action to dispatch failure status for update folder permissions request.
 */
export const updateFolderPermissionsFailed = action('UPDATE_FOLDER_PERMISSIONS_FAILED');

export const onCreateNewFolderSuccess = action(
    'ADD_NEW_FOLDER_TO_STORE',
    (
        folderStore: FolderStore,
        folder: BaseFolderType,
        parentFolderId: string,
        displayName: string
    ) => ({
        folderStore,
        folder,
        parentFolderId,
        displayName,
    })
);

export const onCreateNewFolderInboxRulesResponse = action(
    'ON_CREATE_NEW_FOLDER_INBOX_RULES_RESPONSE',
    (
        folderStore: FolderStore,
        folder: BaseFolderType,
        parentFolderId: string,
        displayName: string
    ) => ({
        folderStore,
        folder,
        parentFolderId,
        displayName,
    })
);
